/**
 * The short-lived Algolia key this browser searches with.
 *
 * The key is signed by the backend and carries its own restrictions — the
 * caller's organization, and the statuses their roles let them read. Nothing
 * here can widen it, which is the point: search runs from the browser straight
 * to Algolia, so any restriction the front end applied would be one the front
 * end could also drop.
 *
 * Keys last an hour. They are refreshed a few minutes early rather than on
 * expiry, because a key that dies mid-keystroke produces an inexplicable
 * "search failed" on a page that was working a second ago. Refreshing also
 * re-reads membership, so a revoked role stops being searchable at the next
 * refresh instead of lasting as long as the tab stays open.
 */

import { useQuery } from '@tanstack/react-query';
import { call } from '../../lib/callable';
import type { SearchKey } from './types';

/** Renew this long before the key actually expires. */
const RENEW_MARGIN_SECONDS = 300;

interface SecuredKeyResponse extends SearchKey {
  success: boolean;
}

export async function fetchSearchKey(): Promise<SearchKey> {
  const r = await call<Record<string, never>, SecuredKeyResponse>(
    'getAlgoliaSecuredKey',
    {},
  );
  return {
    searchKey: r.searchKey,
    appId: r.appId,
    indexName: r.indexName,
    validUntil: r.validUntil,
    canSearchUnpublished: r.canSearchUnpublished,
    boostDepartmentIds: r.boostDepartmentIds ?? [],
    maxResults: r.maxResults,
  };
}

/** Milliseconds until a key should be replaced, floored at zero. */
export function msUntilRenewal(validUntil: number, now = Date.now()): number {
  return Math.max(0, (validUntil - RENEW_MARGIN_SECONDS) * 1000 - now);
}

export function useSearchKey(enabled = true) {
  return useQuery({
    queryKey: ['search-key'],
    enabled,
    queryFn: fetchSearchKey,
    // The key IS the cache entry's lifetime, so both are driven by validUntil
    // rather than by a fixed interval that could outlive it.
    staleTime: (query) => {
      const data = query.state.data as SearchKey | undefined;
      return data ? msUntilRenewal(data.validUntil) : 0;
    },
    refetchInterval: (query) => {
      const data = query.state.data as SearchKey | undefined;
      return data ? Math.max(30_000, msUntilRenewal(data.validUntil)) : false;
    },
    // A search box that cannot get a key should say so once, not hammer a
    // failing callable while somebody types.
    retry: 1,
  });
}
